import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Search, Eye, EyeOff, ChevronLeft } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import toast from "react-hot-toast";

const ProfilePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    notes: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch current user profile
  const {
    data: profileData,
    isLoading,
    error,
  } = useQuery(
    ["profile", user?.id],
    async () => {
      if (!user?.id) return null;
      const response = await api.get("/users/profile");
      return response.data;
    },
    {
      enabled: !!user?.id,
      onSuccess: (response) => {
        if (response?.data) {
          const userData = response.data;
          setFormData({
            first_name: userData.first_name || "",
            last_name: userData.last_name || "",
            email: userData.email || "",
            notes: userData.notes || "",
            password: "",
            confirmPassword: "",
          });
        }
      },
      onError: (error) => {
        toast.error("Failed to load profile data");
      },
    }
  );

  // Update profile mutation
  const updateMutation = useMutation((updateData) => api.put("/users/profile", updateData), {
    onSuccess: () => {
      toast.success("Profile updated successfully");
      queryClient.invalidateQueries(["profile", user?.id]);
      // Clear password fields after successful update
      setFormData((prev) => ({
        ...prev,
        password: "",
        confirmPassword: "",
      }));
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update profile");
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if all required fields are empty
    const allFieldsEmpty = !formData.first_name.trim() && !formData.last_name.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before updating");
      return;
    }

    // Check individual fields and show specific messages
    let hasErrors = false;

    if (!formData.first_name.trim()) {
      toast.error("First name is required");
      hasErrors = true;
    }

    if (!formData.last_name.trim()) {
      toast.error("Last name is required");
      hasErrors = true;
    }

    // If password is provided, validate it
    if (formData.password) {
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        hasErrors = true;
      }
      if (formData.password.length < 6) {
        toast.error("Password must be at least 6 characters long");
        hasErrors = true;
      }
    }

    // If there are validation errors, don't submit
    if (hasErrors) {
      return;
    }

    const updateData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      notes: formData.notes,
    };

    // Only include password if it's provided
    if (formData.password) {
      updateData.password = formData.password;
    }

    updateMutation.mutate(updateData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-0">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mb-4 text-red-500">Failed to load profile</div>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-0">No user logged in</div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-full">
        {/* Form */}
        <div className="slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label
                  htmlFor="first_name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  First Name *
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="last_name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Last Name *
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="pl-3 text-xs font-medium uppercase text-color-gray-500 text-gray-0"
                >
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  className="bg-gray-100 cursor-not-allowed text-gray-0"
                  readOnly
                  disabled
                />
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Leave blank to keep current"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute text-gray-400 transform -translate-y-1/2 hover:text-primary right-3 top-1/2"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="confirmPassword"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute text-gray-400 transform -translate-y-1/2 hover:text-primary right-3 top-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="pl-3 text-xs font-medium uppercase text-gray-0">
                Notes
              </Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add notes here..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Button
                  type="button"
                  onClick={() => navigate(-1)}
                  variant="secondary"
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase border-r rounded-none rounded-b-none border-gray-0 text-gray-0 hover:scale-100"
                >
                  <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                  BACK
                </Button>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
